import { LanguageSelectComponent } from '@common/ui/language-select/language-select.component';
import { NgClass, NgStyle } from '@angular/common';
import { Component, ElementRef, inject, type OnInit, ViewChild } from '@angular/core';
import { FormBuilder, type FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UserInvitationDTO, UserRegistrationWriteDTO } from '@api/models';
import { UserService } from '@api/services/user.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { SharedNavbarService } from '@system/layout/nav-bar/nav-bar.service';
import { ValidationPatternsService } from '@common/services/validation-patterns.service';
import { AuthService } from '@infra/auth/auth.service';
import moment from 'moment';

@Component({
  selector: 'app-user-registration',
  templateUrl: './user-registration.component.html',
  imports: [TranslatePipe, FormsModule, ReactiveFormsModule, NgStyle, NgClass, LanguageSelectComponent],
})
export class UserRegistrationComponent implements OnInit {
  readonly nav = inject(SharedNavbarService);
  private readonly userService = inject(UserService);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly validationPatternsService = inject(ValidationPatternsService);
  private readonly authService = inject(AuthService);
  private readonly translateService = inject(TranslateService);

  @ViewChild('avatarUrl', { static: false }) avatarUrlInput: ElementRef<HTMLInputElement>;

  registration: UserInvitationDTO;
  rdgform: FormGroup;
  validate: boolean;
  displayImage: string;
  userImageUrl: string;
  invitationId: string;
  expireuser = false;

  constructor() {
    this.validate = null;
    this.registration = null;
    this.rdgform = this.createForm();
  }

  ngOnInit(): void {
    this.nav.hide();

    if (this.authService.tokenValid) {
      this.authService.logout(window.location.href);
    }

    this.route.queryParams.subscribe(queryParams => {
      if (queryParams.invitationId) {
        this.invitationId = queryParams.invitationId;
        if (this.invitationId) {
          this.userService.getInvitation(this.invitationId).subscribe(response => {
            const now = moment.utc();
            if (response) {
              this.registration = response;
              if (response.invitationAccepted) {
                this.router.navigate([`/login`]);
              } else if (moment(now).isAfter(response.expiryUtc)) {
                this.expireuser = true;
              } else {
                if (response.avatarUrl) {
                  this.displayImage = response.avatarUrl;
                } else {
                  this.displayImage = '/assets/img/200x200.png';
                }
                this.registration = this.updateFormValues(response);
              }
            }
          });
        } else {
          this.router.navigate(['/']);
        }
      }
    });
  }

  upload(files: FileList): void {
    if (files.length === 0) return;

    const file = files[0];

    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      this.displayImage = e.target.result as string;
    };
    reader.readAsDataURL(file);

    this.userService.uploadAvatar(file).subscribe(response => {
      this.userImageUrl = response;
      this.avatarUrlInput.nativeElement.value = null;
    });
  }

  // update user information from user registration code.
  registerUser(form: FormGroup): void {
    this.validate = true;
    if (form.valid) {
      const request: UserRegistrationWriteDTO = {
        avatarUrl: this.userImageUrl,
        firstName: form.value.firstName,
        lastName: form.value.lastName,
        languageCode: form.value.languageCode,
        phone: form.value.phone,
        password: form.value.password,
      };

      this.userService.registerUser(this.invitationId, request).subscribe(() => {
        this.router.navigate([`/login`]);
      });
    }
  }

  static checkPasswords(group: FormGroup): null | { notSame: boolean } {
    const pass = group.controls.password.value;
    const confirmPass = group.controls.confirmPassword.value;
    const valid = pass === confirmPass;

    if (valid) {
      group.controls.confirmPassword.setErrors(null);
    } else {
      group.controls.confirmPassword.setErrors({ notSame: !valid });
    }

    return valid ? null : { notSame: true };
  }

  updateFormValues(userInvitation: UserInvitationDTO): UserInvitationDTO {
    this.rdgform.patchValue(userInvitation);
    return userInvitation;
  }

  // create form for validation.
  private createForm(): FormGroup {
    const maxLengthNames = 50;
    const passwordValidator = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?!.* )(?=.{8,25})/;

    return this.fb.group(
      {
        firstName: [
          '',
          [
            Validators.required,
            Validators.maxLength(maxLengthNames),
            Validators.pattern(this.validationPatternsService.pattern.userName),
          ],
        ],
        lastName: [
          '',
          [
            Validators.required,
            Validators.maxLength(maxLengthNames),
            Validators.pattern(this.validationPatternsService.pattern.userName),
          ],
        ],
        email: ['', [Validators.required, Validators.pattern(this.validationPatternsService.pattern.email)]],
        phone: [''],
        password: ['', [Validators.required, Validators.pattern(passwordValidator)]],
        confirmPassword: ['', [Validators.required, Validators.pattern(passwordValidator)]],
        languageCode: [this.translateService.getFallbackLang(), Validators.required], // Standard value is English
        avatarUrl: [''],
      },
      { validator: UserRegistrationComponent.checkPasswords }
    );
  }
}
